import type {
  CheckoutEventNames,
  CheckoutLineItem,
  Paddle,
  PaddleEventData,
} from "@paddle/paddle-js";
import { LogSentryException } from "@shapr3d/frontend-lib";

import { paddleBillingClientToken, paddleBillingEnvironment } from "../config";

export const PaddleBillingPaymentType = {
  Card: "card",
  Alipay: "alipay",
  WechatPay: "wechat_pay",
} as const;
export type PaddleBillingPaymentType =
  (typeof PaddleBillingPaymentType)[keyof typeof PaddleBillingPaymentType];
export const PaymentTypes = [
  ...Object.values(PaddleBillingPaymentType),
] satisfies ReadonlyArray<PaddleBillingPaymentType> as [
  PaddleBillingPaymentType,
  ...PaddleBillingPaymentType[],
];

export const PaddleBillingCheckoutEventNames = {
  CHECKOUT_LOADED: "checkout.loaded",
  CHECKOUT_CLOSED: "checkout.closed",
  CHECKOUT_UPDATED: "checkout.updated",
  CHECKOUT_COMPLETED: "checkout.completed",
  CHECKOUT_ERROR: "checkout.error",
  CHECKOUT_FAILED: "checkout.failed",
  CHECKOUT_ITEMS_UPDATED: "checkout.items.updated",
  CHECKOUT_ITEMS_REMOVED: "checkout.items.removed",
  CHECKOUT_CUSTOMER_CREATED: "checkout.customer.created",
  CHECKOUT_CUSTOMER_UPDATED: "checkout.customer.updated",
  CHECKOUT_CUSTOMER_REMOVED: "checkout.customer.removed",
  CHECKOUT_PAYMENT_SELECTED: "checkout.payment.selected",
  CHECKOUT_PAYMENT_INITIATED: "checkout.payment.initiated",
  CHECKOUT_PAYMENT_FAILED: "checkout.payment.failed",
  CHECKOUT_PAYMENT_ERROR: "checkout.payment.error",
  CHECKOUT_DISCOUNT_APPLIED: "checkout.discount.applied",
  CHECKOUT_DISCOUNT_REMOVED: "checkout.discount.removed",
  CHECKOUT_UPSELL_CANCELED: "checkout.upsell.canceled",
} as const satisfies Record<string, `${CheckoutEventNames}`>;

export type PaddleBillingEventCallback = (event: PaddleEventData) => void;

export class PaddleBillingAdapter {
  private static instance: PaddleBillingAdapter = new PaddleBillingAdapter();

  static getInstance(): PaddleBillingAdapter {
    return this.instance;
  }

  private loadPromise: Promise<void> | undefined;
  private eventCallback: PaddleBillingEventCallback | undefined;
  private paddle: Paddle | undefined;

  setEventCallback(callback: PaddleBillingEventCallback): void {
    this.eventCallback = callback;
  }

  async load(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = (async () => {
      try {
        const PaddleJSModule = await import("@paddle/paddle-js");
        const paddle = await PaddleJSModule.initializePaddle({
          environment: paddleBillingEnvironment,
          token: paddleBillingClientToken,
          eventCallback: (event: PaddleEventData) => {
            this.eventCallback?.(event);
          },
        });

        if (!paddle) {
          throw new Error("Paddle.js v2 failed to initialize");
        }

        this.paddle = paddle;
      } catch (error) {
        LogSentryException({
          messagePrefix: "Paddle Billing error",
          error,
          level: "info",
        });
        this.loadPromise = undefined;
        throw error;
      }
    })();

    return this.loadPromise;
  }

  openInlineCheckout({
    customerId,
    priceId,
    containerSelector,
    user: { userId, email, countryCode },
  }: {
    customerId?: string;
    priceId: string;
    containerSelector: string;
    user: {
      userId: string;
      email: string;
      countryCode: string;
    };
  }): void {
    if (!this.paddle) {
      throw new Error("Paddle.js v2 is not loaded");
    }

    this.paddle.Checkout.open({
      settings: {
        allowLogout: false,
        allowedPaymentMethods: ["alipay", "wechat_pay", "card"],
        showAddDiscounts: false,
        variant: "one-page",
        displayMode: "inline",
        frameTarget: containerSelector,
        frameInitialHeight: 450,
        frameStyle: "width: 100%; background-color: transparent; border: none;",
      },
      customer: customerId
        ? {
            id: customerId,
            address: {
              countryCode,
            },
          }
        : {
            email,
            address: {
              countryCode,
            },
          },
      items: [{ priceId, quantity: 1 }],
      customData: { user_id: userId },
    });
  }

  updateCheckoutItems(items: CheckoutLineItem[]): void {
    this.paddle?.Checkout.updateItems(items);
  }

  closeCheckout(): void {
    this.paddle?.Checkout.close();
  }
}
